import { getImageUrl } from '@/utils/assets'
import { useDeviceStore } from '@/stores'

import type {
  BaseScreenCapability,
  BaseScreenPageItem,
  BaseScreenSystemPage,
  DeviceManifest,
  PagePreset,
} from '@rapoo/device-sdk'
import type {
  BuiltInScreenPictureItem,
  PageItem,
  ScreenPictureItem,
  UserScreenPictureItem,
} from './types'

export const MAX_NAME_LENGTH = 8
export const DEFAULT_SCREEN_DELAY = 5
export const SCREEN_PICTURE_ID_BYTES = 16

const FALLBACK_MAX_SCREEN_SLOTS = 8
const FALLBACK_HOME_SLOT = 0

export const BUILT_IN_LOGO_PICTURE_ID = 'Logo'
const BUILT_IN_LOGO_PICTURE_URL = getImageUrl('devices/base/logo.png')

export const FILE_ACCEPTS = ['image/png', 'image/jpeg', 'image/gif'] as const
export const FILE_SUFFIX_ACCEPTS = ['PNG', 'JPG', 'GIF'] as const

export const MIME_EXTENSION_MAP: Record<string, string> = {
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/gif': '.gif',
}

export interface ResolvedBaseScreenPageConfig {
  maxScreenSlots: number
  systemPages: BaseScreenSystemPage[]
  systemSlots: Set<number>
  customSlots: number[]
  maxCustomPages: number
  homePage: BaseScreenSystemPage
  homeSlot: number
}

interface PageDisplayOptions {
  pageConfig: ResolvedBaseScreenPageConfig
  assetsDir?: string
  homePreset?: string
  pictures?: ScreenPictureItem[]
}

function getBaseScreenCapability(
  manifest?: Pick<DeviceManifest, 'capabilities'>,
): BaseScreenCapability | undefined {
  return manifest?.capabilities.find(
    (capability): capability is BaseScreenCapability => capability.type === 'baseScreen',
  )
}

/** 将型号 manifest 的屏显声明归一为 Web 页面槽位策略。 */
export function resolveBaseScreenPageConfig(
  manifest?: Pick<DeviceManifest, 'capabilities'>,
): ResolvedBaseScreenPageConfig {
  const capability = getBaseScreenCapability(manifest)
  const maxScreenSlots = capability?.config.maxScreenSlots ?? FALLBACK_MAX_SCREEN_SLOTS
  const declaredSystemPages = capability?.config.systemPages ?? []
  const fallbackHomePage: BaseScreenSystemPage = {
    slot: FALLBACK_HOME_SLOT,
    kind: 'home',
    presetScreens: [],
  }
  const declaredHomePage = declaredSystemPages.find((page) => page.kind === 'home')
  const homePage = declaredHomePage ?? fallbackHomePage
  const systemPages =
    declaredSystemPages.length === 0
      ? [fallbackHomePage]
      : declaredHomePage
        ? declaredSystemPages
        : [fallbackHomePage, ...declaredSystemPages.filter((page) => page.slot !== homePage.slot)]
  const systemSlots = new Set(systemPages.map((page) => page.slot))
  const customSlots = Array.from({ length: maxScreenSlots }, (_, slot) => slot).filter(
    (slot) => !systemSlots.has(slot),
  )

  return {
    maxScreenSlots,
    systemPages,
    systemSlots,
    customSlots,
    maxCustomPages: customSlots.length,
    homePage,
    homeSlot: homePage.slot,
  }
}

export function getSystemPage(config: ResolvedBaseScreenPageConfig, slot: number) {
  return config.systemPages.find((page) => page.slot === slot)
}

export function isHomeSlot(config: ResolvedBaseScreenPageConfig, slot: number) {
  return config.homeSlot === slot
}

export function isSystemSlot(config: ResolvedBaseScreenPageConfig, slot: number) {
  return config.systemSlots.has(slot)
}

export function isCustomSlot(config: ResolvedBaseScreenPageConfig, slot: number) {
  return config.customSlots.includes(slot)
}

/**
 * 按 manifest 声明的物理槽位顺序查找第一个未使用的自定义槽位。
 * 页面数组本身是播放顺序，不能用它的数组位置决定素材写入槽位。
 */
export function findFirstAvailableCustomSlot(
  pageList: Pick<PageItem, 'id' | 'show' | 'slot'>[],
  config: ResolvedBaseScreenPageConfig,
): number | null {
  for (const slot of config.customSlots) {
    const page = pageList.find((item) => item.slot === slot)
    if (page && !page.id && !page.show) return slot
  }

  return null
}

export function getHomePresets(config: ResolvedBaseScreenPageConfig): PagePreset[] {
  return config.homePage.presetScreens
}

export function getBaseScreen() {
  const deviceStore = useDeviceStore()
  return deviceStore.currentDevice?.features.baseScreen
}

export function getBaseLighting() {
  const deviceStore = useDeviceStore()
  return deviceStore.currentDevice?.features.baseLighting
}

/** 用于构建轮播图默认初始数据。 */
export function createEmptyPages(maxScreenSlots = FALLBACK_MAX_SCREEN_SLOTS): PageItem[] {
  return Array.from({ length: maxScreenSlots }, (_, index) => ({
    id: '',
    slot: -1,
    delay: -1,
    order: index + 1,
    show: false,
    kind: 'custom' as const,
    thumbnail: '',
    thumbname: '',
    thumbext: '',
    error: false,
  }))
}

/** 将 Web 层页面列表转换为 SDK setPages 所需的设备页面结构。 */
export function toDevicePages(pageList: PageItem[]): BaseScreenPageItem[] {
  return pageList.map(({ id, slot, delay, order, show, kind }) => ({
    id,
    slot,
    delay: delay >= 3 ? delay : DEFAULT_SCREEN_DELAY,
    order,
    // 临时新增的自定义页在上传图片前 id 为空，不能写入设备轮播。
    show: kind === 'custom' ? show && Boolean(id) : show,
  }))
}

/** 用于给 SDK getPages 配置项添加 UI 所需的字段 */
function resolvePageDisplay(page: Pick<PageItem, 'id' | 'slot'>, options: PageDisplayOptions) {
  const builtInPicture = options.pictures?.find(
    (picture): picture is BuiltInScreenPictureItem =>
      picture.builtIn === true && picture.id === page.id,
  )
  if (builtInPicture) {
    return {
      thumbnail: builtInPicture.url,
      thumbname: builtInPicture.name,
      thumbext: getPictureExtension(builtInPicture.mimeType),
      error: false,
    }
  }

  const { pageConfig, assetsDir, homePreset, pictures } = options
  const systemPage = getSystemPage(pageConfig, page.slot)

  if (systemPage) {
    const [fixedPreset] = systemPage.presetScreens
    const fixedAsset = fixedPreset?.asset
    const thumbnail =
      systemPage.kind === 'home'
        ? homePreset || ''
        : assetsDir && fixedAsset
          ? getImageUrl(`${assetsDir}/${fixedAsset}`)
          : ''

    return {
      thumbnail,
      thumbname: systemPage.kind === 'mouse-rt' ? 'RT' : '',
      thumbext: '',
      error: false,
    }
  }

  // 尚未分配素材的占位页面（如用户新增但未上传的轮播位），
  // 不应标记为 error，保持空占位状态由 UI 提示选择图像。
  if (!page.id) {
    return {
      thumbnail: '',
      thumbname: '',
      thumbext: '',
      error: false,
    }
  }

  const picture = pictures?.find((item) => item.id === page.id)

  return {
    thumbnail: picture?.url || '',
    thumbname: picture?.name || '',
    thumbext: getPictureExtension(picture?.mimeType),
    error: !picture?.url,
  }
}

function buildPageItem(core: BaseScreenPageItem, options: PageDisplayOptions): PageItem {
  const display = resolvePageDisplay(core, options)
  const systemPage = getSystemPage(options.pageConfig, core.slot)

  return {
    ...core,
    kind: systemPage?.kind ?? 'custom',
    ...display,
    thumbname: systemPage?.kind === 'home' ? core.id : display.thumbname,
  }
}

/** 将 SDK 页面数据补充为 Web 层 PageItem。 */
export function enrichPageItems(
  devicePages: BaseScreenPageItem[],
  options: PageDisplayOptions,
): PageItem[] {
  return devicePages.map((page) => buildPageItem(page, options))
}

/** 保留现有设备字段，仅刷新缩略图等展示字段。 */
export function refreshPageDisplay(pageList: PageItem[], options: PageDisplayOptions): PageItem[] {
  return pageList.map((page) =>
    buildPageItem(
      {
        id: page.id,
        slot: page.slot,
        delay: page.delay,
        order: page.order,
        show: page.show,
      },
      options,
    ),
  )
}

export function createScreenPictureId() {
  const timestamp = Date.now().toString(36)
  const random = Array.from(crypto.getRandomValues(new Uint8Array(4)), (item) =>
    item.toString(36).padStart(2, '0'),
  ).join('')

  return `p${timestamp}${random}`.slice(0, SCREEN_PICTURE_ID_BYTES)
}

export function createUniqueScreenPictureId(existingIds: string[]) {
  let id = createScreenPictureId()

  while (existingIds.includes(id)) {
    id = createScreenPictureId()
  }

  return id
}

export function createDefaultPictureName(index: number) {
  return `素材${String(index).padStart(2, '0')}`.slice(0, MAX_NAME_LENGTH)
}

export function createScreenPictureItem(options: {
  blob: Blob
  mimeType: string
  pictures: ScreenPictureItem[]
}): UserScreenPictureItem {
  const file =
    options.blob instanceof File
      ? options.blob
      : new File([options.blob], `${createDefaultPictureName(options.pictures.length + 1)}.png`, {
          type: options.mimeType,
        })

  return {
    id: createUniqueScreenPictureId(options.pictures.map((item) => item.id)),
    name: createDefaultPictureName(options.pictures.length + 1),
    blob: file,
    mimeType: options.mimeType,
    size: file.size,
    createdAt: Date.now(),
  }
}

/** 应用随包提供的只读素材，作为图片列表中的预设项展示。 */
export function getBuiltInScreenPictures(): BuiltInScreenPictureItem[] {
  return [
    {
      id: BUILT_IN_LOGO_PICTURE_ID,
      name: BUILT_IN_LOGO_PICTURE_ID,
      mimeType: 'image/png',
      size: 0,
      createdAt: 0,
      url: BUILT_IN_LOGO_PICTURE_URL,
      builtIn: true,
    },
  ]
}

/** 获取可交给 SDK 上传的文件；内置素材在实际上传时按需读取。 */
export async function getScreenPictureUploadFile(picture: ScreenPictureItem): Promise<File> {
  if (!picture.builtIn) return picture.blob

  const response = await fetch(picture.url)
  if (!response.ok) {
    throw new Error(`Failed to load built-in screen picture: ${picture.id}`)
  }

  const blob = await response.blob()
  return new File([blob], `${picture.name}.png`, {
    type: blob.type || picture.mimeType,
  })
}

/** 根据素材 mimeType 获取展示用的文件名后缀（含点号），未知类型返回空字符串。 */
export function getPictureExtension(mimeType?: string) {
  if (!mimeType) return ''
  return MIME_EXTENSION_MAP[mimeType] ?? ''
}

/** 按当前数组顺序重排 order，保证与 setPages 写入顺序一致。 */
export function normalizePageOrders(pageList: PageItem[]): PageItem[] {
  return pageList.map((page, index) => ({
    ...page,
    order: index + 1,
  }))
}

/** 将下标收敛到 [0, length - 1]；length 为 0 时返回 0。支持负数倒数索引。 */
function clampPageIndex(index: number, length: number): number {
  if (length <= 0) return 0
  if (index < 0) return Math.max(0, length + index)
  return Math.min(index, length - 1)
}

/**
 * 将页面列表中的指定项移动到目标位置，并按数组顺序重排 order。
 * 不会修改传入的原数组。
 */
export function repositionPageItem(
  pageList: PageItem[],
  locate: { index: number } | { slot: number },
  toIndex: number,
): PageItem[] {
  if (pageList.length === 0) return []

  const fromIndex =
    'index' in locate ? locate.index : pageList.findIndex((page) => page.slot === locate.slot)

  if (fromIndex < 0 || fromIndex >= pageList.length) {
    return normalizePageOrders([...pageList])
  }

  const targetIndex = clampPageIndex(toIndex, pageList.length)
  if (fromIndex === targetIndex) {
    return normalizePageOrders([...pageList])
  }

  const nextPages = [...pageList]
  const [moved] = nextPages.splice(fromIndex, 1)
  nextPages.splice(targetIndex, 0, moved)

  return normalizePageOrders(nextPages)
}

/**
 * 按物理槽位分配新页面，并将它追加到当前播放顺序的末尾。
 * 隐藏项在数组中的位置不参与决定新页面的播放位置。
 */
export function activateFirstAvailableCustomPage(
  pageList: PageItem[],
  config: ResolvedBaseScreenPageConfig,
): { pages: PageItem[]; slot: number } | null {
  const slot = findFirstAvailableCustomSlot(pageList, config)
  if (slot === null) return null

  const newPage = pageList.find((page) => page.slot === slot)
  if (!newPage) return null

  const visiblePages = pageList.filter((page) => page.show)
  const hiddenPages = pageList.filter((page) => !page.show && page.slot !== slot)

  return {
    pages: normalizePageOrders([
      ...visiblePages,
      { ...newPage, show: true, error: false },
      ...hiddenPages,
    ]),
    slot,
  }
}
